import { useEffect, useState } from "react";
import { listMyQuotes, cancelMyQuote } from "@/lib/api";
import { toast } from "sonner";
import { Wrench, X } from "lucide-react";
import { track } from "@/lib/analytics";

const STATUS_STYLE = {
    submitted: "bl-badge bl-badge-neutral",
    reviewing: "bl-badge bl-badge-neutral",
    routed: "bl-badge bl-badge-medium",
    contacted: "bl-badge bl-badge-low",
    won: "bl-badge bl-badge-low",
    lost: "bl-badge bl-badge-high",
    closed: "bl-badge bl-badge-neutral",
    cancelled: "bl-badge bl-badge-neutral",
    new: "bl-badge bl-badge-neutral",
};

function StatusBadge({ status }) {
    const s = status || "submitted";
    return (
        <span className={STATUS_STYLE[s] || "bl-badge bl-badge-neutral"} data-testid={`quote-status-${s}`}>
            {s}
        </span>
    );
}

export default function QuotesSection() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busyId, setBusyId] = useState(null);

    const load = async () => {
        try {
            setLoading(true);
            const r = await listMyQuotes();
            setRows(r || []);
        } catch (e) {
            // not a hard failure — section just shows empty state
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const onCancel = async (quoteId) => {
        const ok = window.confirm(
            "Cancel this quote request? You can submit a new one anytime.",
        );
        if (!ok) return;
        try {
            setBusyId(quoteId);
            await cancelMyQuote(quoteId);
            track("quote_request_cancelled", { quote_id: quoteId });
            toast.success("Quote request cancelled");
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not cancel request");
        } finally {
            setBusyId(null);
        }
    };

    const cancellable = (s) => ["submitted", "reviewing", "new"].includes(s);

    return (
        <div
            className="bl-card p-6 sm:p-8"
            data-testid="account-quotes-section"
            id="quotes"
        >
            <div className="bl-eyebrow w-fit flex items-center gap-1.5">
                <Wrench className="w-3.5 h-3.5" /> Quote requests
            </div>
            <h2 className="mt-3 font-display font-semibold text-2xl text-bl">
                Your repair quote requests
            </h2>
            <p className="mt-2 text-bl-muted text-sm leading-relaxed max-w-2xl">
                Track the status of any repair quotes you&apos;ve requested through Buyer&apos;s
                Leverage. You can cancel any request that hasn&apos;t been routed yet.
            </p>

            <div className="mt-6">
                {loading ? (
                    <div className="text-bl-muted text-sm py-6 text-center">Loading…</div>
                ) : rows.length === 0 ? (
                    <div
                        className="bl-card p-8 text-center bg-bl-bg/30"
                        data-testid="account-quotes-empty"
                    >
                        <p className="text-bl-muted text-sm">
                            You haven&apos;t requested any quotes yet. Open any paid report and
                            click &quot;Get local quotes&quot; to start.
                        </p>
                    </div>
                ) : (
                    <div className="bl-card overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left">
                                    {["Submitted", "Categories", "Status", "ZIP", "Reference", ""].map((h) => (
                                        <th
                                            key={h}
                                            className="px-4 py-3 text-[10px] tracking-[0.2em] uppercase text-bl-muted font-medium border-b border-bl"
                                        >
                                            {h}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((r) => {
                                    const cats =
                                        (r.selected_categories && r.selected_categories.length
                                            ? r.selected_categories
                                            : [r.category]) || [];
                                    return (
                                        <tr
                                            key={r.quote_id}
                                            className="border-b border-bl last:border-0"
                                            data-testid={`quote-row-${r.quote_id}`}
                                        >
                                            <td className="px-4 py-3 text-xs font-mono-bl text-bl-muted whitespace-nowrap">
                                                {r.created_at
                                                    ? new Date(r.created_at).toLocaleDateString()
                                                    : "—"}
                                            </td>
                                            <td className="px-4 py-3 text-bl">
                                                <div className="flex flex-wrap gap-1">
                                                    {cats.map((c) => (
                                                        <span
                                                            key={c}
                                                            className="bl-badge bl-badge-neutral text-[10px]"
                                                        >
                                                            {c}
                                                        </span>
                                                    ))}
                                                </div>
                                            </td>
                                            <td className="px-4 py-3">
                                                <StatusBadge status={r.status} />
                                            </td>
                                            <td className="px-4 py-3 text-bl text-xs font-mono-bl">
                                                {r.zip_code || "—"}
                                            </td>
                                            <td className="px-4 py-3 text-bl-muted text-[10px] font-mono-bl break-all max-w-[160px]">
                                                {r.quote_id}
                                            </td>
                                            <td className="px-4 py-3 text-right">
                                                {cancellable(r.status) ? (
                                                    <button
                                                        onClick={() => onCancel(r.quote_id)}
                                                        disabled={busyId === r.quote_id}
                                                        className="p-1.5 text-bl-muted hover:text-[var(--bl-rust)] disabled:opacity-50"
                                                        title="Cancel request"
                                                        data-testid={`quote-cancel-${r.quote_id}`}
                                                    >
                                                        <X className="w-4 h-4" />
                                                    </button>
                                                ) : (
                                                    <span className="text-bl-muted text-xs">—</span>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}
